import { ChangeEvent, FocusEvent, FormEvent, useEffect, useState } from "react";

type ByKey<T> = Record<string, T>;
type MergedFormElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type Validator = { func: (val?: any) => boolean, error: string }
type RegisterOptions = { required?: string, readOnly?: boolean, validators?: ByKey<Validator>; }
type SubmitOptions = { resetValues?: boolean, resetStateSubmitTimer?: number }
type FormState = {
  isDirty: boolean;
  isValid: boolean;
  dirtyFields: ByKey<{ type: string, error: string }>;
  submit: {
    inProcess: boolean;
    succeeded: boolean;
    finished: boolean;
  }
}

// todo: check if work with array
const getByPath = <T extends object, R = unknown>(obj: T, path: string): R | undefined =>
  path.split(".").reduce<any>((acc, key) => acc?.[key], obj || {});

// todo: check if work with array
const setByPath = <T extends object>(obj: T, path: string, value: any): void => {
  const keys = path.split(".");
  const last = keys.pop()!;
  if (!last) return;
  const target = keys.reduce<any>((acc, key) => {
    if (acc[key] === undefined) acc[key] = {};
    return acc[key];
  }, obj);
  target[last] = value;
}

const flatten = (obj: any, parentKey: string = ""): ByKey<any> => {
  if (obj === null)
    return parentKey ? { [parentKey]: obj } : {};
  if (Array.isArray(obj)) {
    return obj.reduce((acc, cur, index) => {
      const fullKey = parentKey ? `${parentKey}.${index}`: `${index}`;
      return {
        ...acc,
        ...(typeof cur === "object") && { ...flatten(cur, fullKey) },
        ...(typeof cur !== "object") && { [fullKey]: cur }
      };
    }, {})
  }
  if (typeof obj === "object") {
    return Object.entries(obj).reduce((acc, [ key, cur ]) => {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      return {
        ...acc,
        ...(typeof cur === "object") && { ...flatten(cur, fullKey) },
        ...(typeof cur !== "object") && { [fullKey]: cur }
      }
    }, {});
  }
  return parentKey ? { [parentKey]: obj } : {};
}

export const useForm = <TValue>() => {

  const [ init, setInit ] = useState<boolean>(true);
  const [ submitting, setSubmitting ] = useState<boolean>(false);
  const [ failed, setFailed ] = useState<boolean>(false);
  const [ succeeded, setSucceeded ] = useState<boolean>(false);

  const [ values, setValues ] = useState<Partial<TValue>>({});
  const [ touched, setTouched ] = useState<ByKey<boolean>>({});
  const [ validators, setValidators ] = useState<ByKey<ByKey<Validator>>>({})
  const [ errors, setErrors ] = useState<ByKey<ByKey<string>>>({});


  // effects

  // refresh errors state on values changes
  useEffect(() => {
    Object
      .entries(flatten(values)).map(([ name, value ]) => ([ name, value, validators[name] ]))
      .forEach((args: any) => checkValidators.apply(this, args));
  }, [ values ]);


  const checkValidators = (name: string, value: any, fieldValidators?: ByKey<Validator>) => {
    const fieldCheck = Object.entries(fieldValidators || {}).reduce<ByKey<string>>((err, [ key, validator ]) => ({
      ...err,
      ...(!validator.func(value) && { [key]: validator.error })
    }), {});
    setErrors(prev => {
      const { [name]: current, ...others } = prev;
      return { ...others, ...(Object.values(fieldCheck).length > 0) && { [name]: fieldCheck } };
    });
  }


  // dom interactions

  const handleChange = (e: ChangeEvent<MergedFormElement>) => {
    const { name, value } = e.target;
    const newValue = value === "" ? undefined : value;
    const change = { ...values };
    setByPath(change, name, newValue);
    setValues(change);
  }

  const handleBlur = (e: FocusEvent<MergedFormElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  }

  const handleSubmit = (process: (data: Partial<TValue>) => Promise<void>, options?: SubmitOptions) =>
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setSubmitting(true);
      failed && setFailed(false);
      succeeded && setSucceeded(false);
      await process(values)
        .then(() => {
          setSucceeded(true);
          !!options?.resetValues && reset();
        })
        .catch(() => setFailed(true))
        .finally(() => {
          setSubmitting(false);
          !!options?.resetStateSubmitTimer && setTimeout(() => {
            setSubmitting(false);
            setFailed(false);
            setSucceeded(false);
          }, options.resetStateSubmitTimer);
        });
    }

  // result

  const register = (name: string, opt?: RegisterOptions) => {

    // save validators and apply check on first usage
    const current = {
      ...(!!opt?.required) && { required: { func: (val?: any) => typeof val !== "undefined", error: opt.required } },
      ...opt?.validators
    };
    if (!(name in validators)) {
      setValidators(prev => ({ ...prev, [name]: current }));
      checkValidators(name, undefined, current);
    }
    init && setInit(false);

    // attributes to apply into HTMLElement on registration
    return {
      name,
      ...(!!opt?.required) && { required: true },
      ...(!!opt?.readOnly) && { readOnly: opt.readOnly, disabled: opt.readOnly },
      value: (getByPath(values || {}, name) as string | undefined) || "",
      onChange: handleChange,
      onBlur: handleBlur
    };

  }

  const watch = (name?: string): TValue | Partial<TValue> | string | undefined =>
    !!name ? getByPath(values, name) : values;

  const reset = (val?: Partial<TValue>) => {
    setValues(val || {});
    setTouched({});
  }

  const formState: FormState = {
    isDirty: init || Object.values(touched).length === 0,
    isValid: init ? false : Object.values(errors).length === 0,
    dirtyFields: Object
      .entries(errors)
      .filter(([ name, _ ]) => Object.keys(touched).includes(name))
      .reduce((acc, [ name, errors ]) => {
        const [ type, error ] = Object.entries(errors)[0];
        return { ...acc, [name]:  { type, error }};
        }, {}),
    submit: {
      inProcess: submitting,
      finished: !submitting && (succeeded || failed),
      succeeded: !submitting && succeeded
    }
  };

  return { register, watch, handleSubmit, reset, formState };

}
